import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Card, Form, InputNumber, Button, Tabs, Table, Tag, Typography,
    Space, Spin, Alert, Badge, Tooltip, Input, Empty, Row, Col, Statistic,
} from 'antd';
import {
    RocketOutlined, SearchOutlined, InfoCircleOutlined, StarOutlined,
    CheckCircleOutlined, ReloadOutlined, FilePdfOutlined,
    UserOutlined, CalendarOutlined,
} from '@ant-design/icons';
import UserLayout from '../Layout/UserLayout';
import axiosInstance from '../../utils/axiosConfig';

const { Title, Text } = Typography;

const NAM_TUYEN_SINH = 2026;

const TAG_CONFIG = {
    vhigh:    { label: 'Cơ hội rất cao', color: '#27ae60', bg: '#eafaf1', icon: <RocketOutlined /> },
    high:     { label: 'Cơ hội cao',     color: '#d35400', bg: '#fef5ec', icon: <StarOutlined /> },
    possible: { label: 'Có cơ hội',      color: '#2980b9', bg: '#eaf4fb', icon: <CheckCircleOutlined /> },
};

const METHOD_LABELS = {
    thpt: 'THPT',
    hb:   'Học bạ',
    tsa:  'TSA',
    hsa:  'HSA',
    spt:  'SPT',
};

const METHOD_LIMITS = {
    thpt: { min: 0, max: 30 },
    hb:   { min: 0, max: 30 },
    tsa:  { min: 0, max: 100 },
    hsa:  { min: 0, max: 150 },
    spt:  { min: 0, max: 30 },
};

// ─── Sub-component: bảng kết quả cho mỗi tab ────────────────────────────────

// Ẩn điểm của HB của một số mã ngành
const IGNORE_DISPLAY = ['GTADKLG2']

const ResultTable = ({ data, tag }) => {
    console.log('data', data)
    const { color } = TAG_CONFIG[tag];

    const deltaRender = (delta) => {
        const sign = delta >= 0 ? '+' : '';
        return (
            <Text strong style={{ color: delta >= 0 ? '#27ae60' : '#c0392b' }}>
                {sign}{delta.toFixed(2)}
            </Text>
        );
    };

    const scoreRender = (v, r) =>
        v != null && !IGNORE_DISPLAY.includes(r.ma_xt)
            ? <Text style={{ fontSize: 13 }}>{Number(v).toFixed(2)}</Text>
            : <Text type="secondary">—</Text>;

    const columns = [
        {
            title: '#',
            width: 40,
            render: (_, __, i) => <Text type="secondary" style={{ fontSize: 12 }}>{i + 1}</Text>,
        },
        {
            title: 'Mã XT',
            dataIndex: 'ma_xt',
            width: 130,
            render: (v, r) => (
                <Space direction="vertical" size={2}>
                    <Text strong style={{ fontSize: 12, color }}>{v}</Text>
                    {r.is_new && <Tag color="purple" style={{ fontSize: 10, marginRight: 0 }}>Ngành mới 2026</Tag>}
                </Space>
            ),
        },
        {
            title: 'Tên chuyên ngành',
            dataIndex: 'ten_chuyen_nganh',
            ellipsis: { showTitle: true },
            render: (v, r) => {
                return (
                        <>
                    {/* <Tooltip
                        title={
                            <Space direction="vertical" size={2}>
                                {r.ma_xt && <span>Mã ngành: {r.ma_xt}</span>}
                                {r.to_hop_xt && <span>Tổ hợp: {r.to_hop_xt}</span>}
                            </Space>
                        }
                    > */}
                        <Text style={{ fontSize: 13 }}>{v}</Text>
                    {/* </Tooltip>                 */}
                </> 
                )
               
                
            },
        },
        {
            title: 'Điểm chuẩn 2025',
            align: 'center',
            children: [
                { title: 'THPT',   dataIndex: 'diem_thpt', width: 70, align: 'center', render: scoreRender },
                { title: 'Học bạ', dataIndex: 'diem_hb',   width: 70, align: 'center', render: scoreRender },
                { title: 'TSA',    dataIndex: 'diem_tsa',  width: 70, align: 'center', render: scoreRender },
                { title: 'HSA',    dataIndex: 'diem_hsa',  width: 70, align: 'center', render: scoreRender },
                { title: 'SPT',    dataIndex: 'diem_spt',  width: 70, align: 'center', render: scoreRender },
            ],
        },
        {
            title: 'Tổ hợp điểm cao nhất để xét',
            dataIndex: 'best_method',
            width: 130,
            align: 'center',
            render: (v, r) => v ? (
                <Space>
                    <Tag color="blue">{v}</Tag>
                    <Text style={{ fontSize: 12 }}>{(r.user_score_raw ?? r.user_score)?.toFixed(2)}</Text>
                </Space>
            ) : <Text type="secondary">—</Text>,
        },
        // {
        //     title: (
        //         <Tooltip title="Chênh lệch giữa điểm quy đổi của bạn so với điểm chuẩn THPT">
        //             <Space>Độ lệch <InfoCircleOutlined /></Space>
        //         </Tooltip>
        //     ),
        //     dataIndex: 'delta',
        //     width: 90,
        //     align: 'center',
        //     sorter: (a, b) => b.delta - a.delta,
        //     defaultSortOrder: 'ascend',
        //     render: deltaRender,
        // },
    ];

    if (data.length === 0) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<Text type="secondary">Không có ngành nào trong nhóm này</Text>}
            />
        );
    }

    return (
        <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            size="small"
            bordered
            pagination={data.length > 15 ? { pageSize: 20, showTotal: (t) => `${t} ngành` } : false}
            scroll={{ x: 800 }}
        />
    );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const AdmissionEvaluatorPage = () => {
    const [form] = Form.useForm();

    // Pre-fill state
    const [prefill, setPrefill]         = useState(null);
    const [prefillLoading, setPrefillLoading] = useState(true);

    // Result state
    const [results, setResults]         = useState(null);
    const [summary, setSummary]         = useState(null);
    const [counts, setCounts]           = useState(null);
    const [evaluating, setEvaluating]   = useState(false);
    const [exporting, setExporting]     = useState(false);
    const [lastScores, setLastScores]   = useState(null);

    // Filter/search state (chạy hoàn toàn trên FE)
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab]     = useState('vhigh');

    // Load pre-fill khi mở trang
    useEffect(() => {
        axiosInstance.get('/api/v1/admission/pre-fill')
            .then((res) => {
                setPrefill(res.data);
                if (res.data.hb_score) {
                    form.setFieldValue('hb', res.data.hb_score);
                }
            })
            .catch(() => {})
            .finally(() => setPrefillLoading(false));
    }, [form]);

    const handleEvaluate = async () => {
        try {
            const values = await form.validateFields();
            const scores = {
                thpt: values.thpt || null,
                hb:   values.hb   || null,
                tsa:  values.tsa  || null,
                hsa:  values.hsa  || null,
                spt:  values.spt  || null,
            };

            const hasAny = Object.values(scores).some((v) => v != null && v > 0);
            if (!hasAny) {
                form.setFields([{ name: 'thpt', errors: ['Vui lòng nhập ít nhất một điểm.'] }]);
                return;
            }

            setEvaluating(true);
            setResults(null);
            setSearchQuery('');

            const res = await axiosInstance.post('/api/v1/admission/evaluate', {
                scores,
                nam_tuyen_sinh: NAM_TUYEN_SINH,
            });

            setResults(res.data.results);
            setSummary(res.data.summary);
            setCounts(res.data.counts);
            setLastScores(scores);
            setActiveTab('vhigh');
        } catch (err) {
            if (!err?.errorFields) {
                // Lỗi API
            }
        } finally {
            setEvaluating(false);
        }
    };

    const handleExportPdf = async () => {
        if (!lastScores) return;
        setExporting(true);
        try {
            const res = await axiosInstance.post(
                '/api/v1/admission/export-pdf',
                { scores: lastScores, nam_tuyen_sinh: NAM_TUYEN_SINH },
                { responseType: 'blob' }
            );
            const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href  = url;
            link.download = `co-hoi-xet-tuyen-${NAM_TUYEN_SINH}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } catch {
            // silent – user sẽ thấy không có file
        } finally {
            setExporting(false);
        }
    };

    // Filter + search trên FE
    const filteredByTag = useCallback((tag) => {
        if (!results) return [];
        let byTag = results.filter((r) => r.tag === tag);
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            byTag = byTag.filter(
                (r) =>
                    r.ma_xt.toLowerCase().includes(q) ||
                    r.ten_chuyen_nganh.toLowerCase().includes(q) ||
                    (r.ma_nganh && r.ma_nganh.toLowerCase().includes(q))
            );
        }
        // Sắp xếp theo điểm chuẩn quy đổi về thang THPT (= user_score - delta), cao → thấp
        return [...byTag].sort((a, b) => {
            const benchA = (a.user_score ?? 0) - (a.delta ?? 0);
            const benchB = (b.user_score ?? 0) - (b.delta ?? 0);
            return benchB - benchA;
        });
    }, [results, searchQuery]);

    const tabItems = useMemo(() => (
        ['vhigh', 'high', 'possible'].map((tag) => {
            const { label, color, icon } = TAG_CONFIG[tag];
            const count = counts?.[tag] ?? 0;
            return {
                key: tag,
                label: (
                    <Space>
                        <span style={{ color }}>{icon}</span>
                        <span>{label}</span>
                        {counts && (
                            <Badge
                                count={count}
                                style={{ backgroundColor: count > 0 ? color : '#d9d9d9' }}
                            />
                        )}
                    </Space>
                ),
                children: (
                    <ResultTable data={filteredByTag(tag)} tag={tag} />
                ),
            };
        })
    ), [counts, filteredByTag]);

    const hasResults = results !== null;

    return (
        <UserLayout title="Đánh giá cơ hội trúng tuyển">
            <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 8px' }}>

                {/* ── Form nhập điểm ── */}
                <Card
                    title={
                        <Space>
                            <RocketOutlined style={{ color: '#1890ff' }} />
                            <span>Nhập điểm để phân tích cơ hội</span>
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <div style={{
                        background: '#fffbe6',
                        border: '1px solid #ffe58f',
                        borderRadius: 8,
                        padding: '16px',
                        marginBottom: 16,                        
                    }}>
                        <InfoCircleOutlined style={{ fontSize: 22, color: '#faad14', marginBottom: 8, display: 'block' }} />
                        <Text style={{ fontSize: 13, color: '#595959', display: 'block' }}>
                            - Thông tin chỉ mang tính chất tham khảo dựa trên điểm chuẩn theo phương thức xét học bạ năm 2025 của Trường và dữ liệu thí sinh cung cấp.
                        </Text>
                        <Text style={{ fontSize: 13, color: '#595959', display: 'block', marginTop: 6 }}>
                            - Để được xét tuyển chính thức vào Trường, thí sinh bắt buộc phải đăng kí trên hệ thống của Bộ GD&ĐT từ ngày 02/7/2026 đến trước 17h00 ngày 14/7/2026.
                        </Text>
                        <Text style={{ fontSize: 13, color: '#595959', display: 'block', marginTop: 4 }}>
                            - Mã trường: <Text strong>GTA</Text> (Học tại Hà Nội) — <Text strong>GTP</Text> (Học tại Phú Thọ)
                        </Text>
                        <Text style={{ fontSize: 13, color: '#595959', display: 'block', marginTop: 4 }}>
                            - Điểm xét tuyển học bạ chính thức sẽ được tính toán lại sau khi đối chiếu dữ liệu với Bộ GD&ĐT.
                        </Text>
                    </div>

                    {prefillLoading ? (
                        <Spin tip="Đang tải thông tin..." />
                    ) : (
                        <>
                            {/* ── Thông tin cá nhân thí sinh ── */}
                            {prefill?.full_name && (
                                <div style={{
                                    background: '#f0f5ff',
                                    border: '1px solid #adc6ff',
                                    borderRadius: 8,
                                    padding: '10px 16px',
                                    marginBottom: 16,
                                }}>
                                    <Row align="middle" gutter={[20, 4]} wrap>
                                        <Col>
                                            <Space size={6}>
                                                <UserOutlined style={{ color: '#2f54eb' }} />
                                                 Họ và tên:<Text strong style={{ color: '#2f54eb', fontSize: 14 }}>
                                                   {prefill.full_name}
                                                </Text>
                                            </Space>
                                        </Col>
                                        {prefill.date_of_birth && (
                                            <Col>
                                                <Text style={{ fontSize: 13, color: '#595959' }}>
                                                    Ngày sinh: <Text strong>{prefill.date_of_birth}</Text>
                                                </Text>
                                            </Col>
                                        )}
                                        {prefill.graduation_year && (
                                            <Col>
                                                <Space size={4}>
                                                    <CalendarOutlined style={{ color: '#595959', fontSize: 12 }} />
                                                    <Text style={{ fontSize: 13, color: '#595959' }}>
                                                        Tốt nghiệp: <Text strong>{prefill.graduation_year}</Text>
                                                    </Text>
                                                </Space>
                                            </Col>
                                        )}
                                    </Row>
                                </div>
                            )}

                            <Form form={form} layout="vertical">
                                <Row gutter={[16, 0]} wrap>
                                    {Object.entries(METHOD_LIMITS).map(([method, { min, max }]) => (
                                        <Col key={method} xs={12} sm={8} md={4} lg={4}>
                                            <Form.Item
                                                name={method}
                                                label={
                                                    <Space size={4}>
                                                        <span>{METHOD_LABELS[method]}</span>
                                                        {method === 'hb' && prefill?.hb_score && (
                                                            <Tooltip title="Tính từ bảng điểm học bạ trong hồ sơ của bạn">
                                                                <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                                            </Tooltip>
                                                        )}
                                                        {['tsa', 'hsa', 'spt'].includes(method) && prefill?.[`has_${method}`] && (
                                                            <Tooltip title={`Bạn đã khai báo tham dự kỳ thi ${METHOD_LABELS[method]}`}>
                                                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                            </Tooltip>
                                                        )}
                                                    </Space>
                                                }
                                                rules={[
                                                    {
                                                        validator: (_, v) =>
                                                            !v || (v >= min && v <= max)
                                                                ? Promise.resolve()
                                                                : Promise.reject(`0 – ${max}`),
                                                    },
                                                ]}
                                            >
                                                <InputNumber
                                                    min={min}
                                                    max={max}
                                                    step={0.01}
                                                    precision={2}
                                                    style={{ width: '100%' }}
                                                    placeholder={`0 – ${max}`}
                                                />
                                            </Form.Item>
                                        </Col>
                                    ))}
                                </Row>

                                <Row gutter={12}>
                                    <Col flex="1">
                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<RocketOutlined />}
                                            onClick={handleEvaluate}
                                            loading={evaluating}
                                            block
                                        >
                                            Phân tích cơ hội
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button
                                            size="large"
                                            icon={<ReloadOutlined />}
                                            onClick={() => {
                                                form.resetFields();
                                                setResults(null);
                                                setSummary(null);
                                                setCounts(null);
                                                setSearchQuery('');
                                                if (prefill?.hb_score) {
                                                    form.setFieldValue('hb', prefill.hb_score);
                                                }
                                            }}
                                        >
                                            Làm lại
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </>
                    )}
                </Card>

                {/* ── Kết quả ── */}
                {evaluating && (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin size="large" tip="Đang phân tích..." />
                    </div>
                )}

                {hasResults && !evaluating && (
                    <>
                        {/* Summary badge */}
                        {summary && (
                            <Alert
                                type="success"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message={
                                    <span>
                                        Phương thức tốt nhất của bạn:{' '}
                                        <Text strong>{summary.best_method_name}</Text>                                        
                                        {summary.benchmark_year && (
                                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                                — Tham chiếu điểm chuẩn {summary.benchmark_year}
                                            </Text>
                                        )}
                                    </span>
                                }
                            />
                        )}

                        {/* Search + Export */}
                        <Row gutter={8} style={{ marginBottom: 16 }}>
                            <Col flex="1">
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder="Tìm theo tên ngành, mã xét tuyển..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    allowClear
                                />
                            </Col>
                            <Col>
                                <Button
                                    icon={<FilePdfOutlined />}
                                    loading={exporting}
                                    onClick={handleExportPdf}
                                    style={{ color: '#cf1322', borderColor: '#ffa39e', background: '#fff1f0' }}
                                >
                                    Tải PDF
                                </Button>
                            </Col>
                        </Row>

                        {/* <Row gutter={12} style={{ marginBottom: 16 }}>
                            {[
                                {
                                    label: 'Tổng có cơ hội',
                                    value: (counts?.vhigh ?? 0) + (counts?.high ?? 0) + (counts?.possible ?? 0),
                                    color: '#1890ff',
                                    bg:    '#e6f4ff',
                                    border:'#91caff',
                                },
                                { label: 'Cơ hội rất cao', value: counts?.vhigh    ?? 0, color: TAG_CONFIG.vhigh.color,    bg: TAG_CONFIG.vhigh.bg,    border: '#95de64' },
                                { label: 'Cơ hội cao',     value: counts?.high     ?? 0, color: TAG_CONFIG.high.color,     bg: TAG_CONFIG.high.bg,     border: '#ffbb96' },
                                { label: 'Có cơ hội',      value: counts?.possible ?? 0, color: TAG_CONFIG.possible.color, bg: TAG_CONFIG.possible.bg, border: '#91caff' },
                            ].map(({ label, value, color, bg, border }) => (
                                <Col key={label} xs={12} sm={6}>
                                    <div style={{
                                        background: bg,
                                        border: `1px solid ${border}`,
                                        borderRadius: 8,
                                        padding: '12px 16px',
                                        textAlign: 'center',
                                    }}>
                                        <Statistic
                                            value={value}
                                            suffix="ngành"
                                            valueStyle={{ color, fontSize: 22, fontWeight: 700 }}
                                        />
                                        <Text style={{ fontSize: 12, color: '#595959' }}>{label}</Text>
                                    </div>
                                </Col>
                            ))}
                        </Row> */}

                        {/* Tabs kết quả */}
                        <Tabs
                            activeKey={activeTab}
                            onChange={(key) => { setActiveTab(key); setSearchQuery(''); }}
                            items={tabItems}
                            type="card"
                        />
                    </>
                )}
            </div>
        </UserLayout>
    );
};

export default AdmissionEvaluatorPage;
